"use client";

import { type ModuleTeamCarousel } from "@lib/types";
import { motion } from "framer-motion";
import { useState } from "react";

import PortableText from "@/components/shared/portable-text";
import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/ui/cn";
import { getProseClasses } from "@/lib/ui/colorTheme";

import { TeamCarouselModal } from "./Modal";

type TeamCarouselCardProps = {
  item: NonNullable<ModuleTeamCarousel["items"]>[number];
  isActive: boolean;
  onHover?: () => void;
  onTouch?: () => void;
  textColorClasses?: string;
};

export function TeamCarouselCard({
  item,
  isActive,
  onHover,
  onTouch,
  textColorClasses,
}: TeamCarouselCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasInactiveImage = !!item.inactiveImage?.image;

  // Only apply grayscale if no inactive image is provided
  const shouldApplyGrayscale = !isActive && !hasInactiveImage;

  return (
    <>
      <motion.div
        className={cn(
          "relative flex flex-col overflow-hidden rounded-lg transition-all duration-300 ease-out",
          isActive
            ? "tablet:w-[420px] tablet:h-[420px] tablet:max-h-none tablet:max-w-none desktop:w-[544px] desktop:h-[544px] h-[100vw] max-h-[394px] w-full max-w-[394px]"
            : "tablet:block tablet:w-[210px] tablet:h-[420px] desktop:w-[272px] desktop:h-[544px] hidden",
        )}
        onMouseEnter={onHover}
        onTouchStart={onTouch}
      >
        {(item.image || hasInactiveImage) && (
          <div className="relative h-full w-full overflow-hidden">
            <motion.div
              className="relative h-full"
              initial={{
                width: "200%",
                x: "-25%",
              }}
              animate={{
                width: isActive ? "100%" : "200%",
                x: isActive ? 0 : "-25%",
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                duration: 0.4,
              }}
            >
              {/* Active image — always in the DOM, shown when active */}
              {item.image && (
                <motion.div
                  key="active-image"
                  className="absolute inset-0"
                  initial={{ opacity: hasInactiveImage ? 0 : 1 }}
                  animate={{
                    opacity: isActive || !hasInactiveImage ? 1 : 0,
                    filter: shouldApplyGrayscale ? "grayscale(100%)" : "grayscale(0%)",
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                    duration: 0.4,
                  }}
                >
                  <ResponsiveImage
                    key="active-img"
                    src={item.image}
                    fill
                    className="object-contain object-center"
                    imageOptions={{ desktopWidth: 400 }}
                  />
                </motion.div>
              )}

              {/* Inactive image — always in the DOM, shown when inactive */}
              {hasInactiveImage && (
                <motion.div
                  key="inactive-image"
                  className="absolute inset-0"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: isActive ? 0 : 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                    duration: 0.4,
                  }}
                >
                  <ResponsiveImage
                    key="inactive-img"
                    src={item.inactiveImage}
                    fill
                    className="object-contain object-center"
                    imageOptions={{ desktopWidth: 400 }}
                  />
                </motion.div>
              )}
            </motion.div>
          </div>
        )}

        <motion.div
          className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6"
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: isActive ? 1 : 0,
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            duration: 0.4,
          }}
        >
          {item.content && (
            <div className={textColorClasses || "text-white"}>
              <PortableText
                value={item.content}
                className={cn("prose prose-p:my-0 typography-text-medium-normal", textColorClasses)}
              />
            </div>
          )}

          {item.bio && item.bio.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsModalOpen(true)}
              className="mt-4 w-fit"
            >
              Read Bio
            </Button>
          )}
        </motion.div>
      </motion.div>

      <TeamCarouselModal
        item={item}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        textColorClasses={getProseClasses(false)}
      />
    </>
  );
}
